import { Injectable, OnDestroy } from '@angular/core';
import * as SplitIO from '@splitsoftware/splitio-browserjs/types/splitio';
import { SplitFactory } from '@splitsoftware/splitio-browserjs';
import { environment } from 'src/environments/environment';

/***
  Notes of how to use it:
  1. Declare `private splitService: SplitService` in the constructor of the component or service where you want to use Split.io.
  2. Call getTreatment method of the splitService instance to get the treatment for a feature flag.
  3. The getTreatment method returns a promise that resolves to the treatment value. (control, on, off, or custom value)

  Example:
  async test() {
    const treatment = await this.splitService.getTreatment('test');
    console.log('Treatment:', treatment);
  }
***/

@Injectable({
  providedIn: 'root',
})
export class SplitIoService implements OnDestroy {
  factory: SplitIO.IBrowserSDK | null = null;
  client: SplitIO.IBrowserClient | null = null;
  isSdkReady: boolean = false;

  userUuid: string = '';
  accountUuid: string = '';
  roleIds: string = '';
  attributes = {};

  constructor() {
    this.setAttributes();
  }

  setAttributes(customAttributes?: Record<string, string>): void {
    // Initialize user and account UUIDs
    this.userUuid = localStorage.getItem('user_uuid') || new URLSearchParams(window.location.search).get('user_uuid') || '';
    this.accountUuid = localStorage.getItem('current_account_uuid') || '';
    this.roleIds = this.getUserRoleIds();
    this.attributes = {
      user_uuid: this.userUuid,
      account_uuid: this.accountUuid,
      role_ids: this.roleIds,
      environment: environment.envName,
      ...customAttributes,
    };
    if (this.client && this.isSdkReady) {
      this.client.setAttributes(this.attributes);
    }
  }

  // Initialize Split.io for pre-auth context (login page, before user_uuid is available)
  initSplitPreAuth(): void {
    if (this.factory && this.client) {
      console.warn('splitio => SDK already initialized, skipping pre-auth init.');
      return;
    }

    console.log('splitio => Initializing Split in pre-auth mode.');

    this.factory = SplitFactory({
      core: {
        authorizationKey: environment.splitIO,
        key: 'login-page',
      },
      debug: false,
    });

    this.client = this.factory.client();

    this.client.on(this.client.Event.SDK_READY, () => {
      this.isSdkReady = true;
      console.log('splitio => Pre-auth SDK is ready.');
    });

    this.client.on(this.client.Event.SDK_READY_TIMED_OUT, () => {
      console.error('splitio => Pre-auth SDK timed out.');
    });
  }

  // Destroy the pre-auth client and reinitialize Split.io with the authenticated user UUID
  initSplitUser(userUuid: string): void {
    if (!userUuid) {
      console.error('splitio => Cannot initialize user context: userUuid is empty.');
      return;
    }

    if (this.factory && this.client && this.userUuid === userUuid) {
      console.warn('splitio => Split already initialized for this user.');
      return;
    }

    if (this.client) {
      this.client.destroy();
      this.client = null;
      this.factory = null;
      this.isSdkReady = false;
      console.log('splitio => Previous client destroyed, reinitializing for user context.');
    }

    this.userUuid = userUuid;
    this.accountUuid = localStorage.getItem('current_account_uuid') || '';
    this.roleIds = this.getUserRoleIds();
    this.attributes = {
      user_uuid: this.userUuid,
      account_uuid: this.accountUuid,
      role_ids: this.roleIds,
      environment: environment.envName,
    };

    console.log('splitio => Initializing Split for user context.');

    this.factory = SplitFactory({
      core: {
        authorizationKey: environment.splitIO,
        key: this.userUuid,
      },
      debug: false,
    });

    this.client = this.factory.client();

    this.client.on(this.client.Event.SDK_READY, () => {
      this.isSdkReady = true;
      console.log('splitio => User SDK is ready.');
      this.client!.setAttributes(this.attributes);
      this.client!.track('user', 'app_loaded', 0, this.attributes);
    });

    this.client.on(this.client.Event.SDK_READY_TIMED_OUT, () => {
      console.error('splitio => User SDK failed to initialize within timeout.');
    });
  }

  // Initialize Split.io
  initSplit(): void {
    if (!this.userUuid) {
      console.error('splitio => User UUID not found, splitio did not start.');
      return;
    }

    if (this.factory && this.client) {
      console.warn('Split.io SDK is already initialized.');
      return;
    }

    console.warn('Split.io SDK is initializing...', environment.splitIO);
    console.warn('Split.io SDK is user...', this.userUuid);

    this.factory = SplitFactory({
      core: {
        authorizationKey: environment.splitIO,
        key: this.userUuid,
      },
      debug: false,
    });

    this.client = this.factory.client();

    this.client.on(this.client.Event.SDK_READY, () => {
      this.isSdkReady = true;
      console.log('splitio => SDK is ready');
      this.client!.setAttributes(this.attributes);
      this.client!.track('user', 'app_loaded', 0, this.attributes);
      console.log('splitio => SDK initialized');
    });

    this.client.on(this.client.Event.SDK_READY_TIMED_OUT, () => {
      console.error('splitio => SDK failed to initialize within timeout.');
    });
  }

  // Public method to get treatment for a feature flag
  async getTreatment(flagName: string): Promise<string> {
    if (!this.client) {
      console.error('splitio => SDK is not initialized.');
      return 'control';
    }

    return new Promise((resolve) => {
      this.retryGetTreatment(flagName, 10, 1000, resolve);
    });
  }

  // Recursive retry mechanism to get treatment
  private retryGetTreatment(
    flagName: string,
    retries: number,
    delay: number,
    resolve: (value: string) => void
  ) {
    if (this.isSdkReady) {
      this.client!.track('user', `get_treatment_${flagName}`);
      console.log(
        `splitio => get_treatment event tracked for flag [${flagName}]`
      );
      resolve(this.client!.getTreatment(flagName, this.attributes));
    } else if (retries > 0) {
      console.log(`splitio => SDK not ready. Retrying in ${delay}ms...`);
      setTimeout(() => {
        this.retryGetTreatment(flagName, retries - 1, delay, resolve);
      }, delay);
    } else {
      console.log('splitio => SDK not ready after retries, returning control.');
      resolve('control');
    }
  }

  // Cleanup when the service is destroyed
  ngOnDestroy(): void {
    if (this.client) {
      this.client.destroy();
      console.log('splitio => SDK destroyed');
    }
  }

  getUserRoleIds(): string {
    const jsonRoles = localStorage.getItem('roles');
    if (jsonRoles) {
      try {
        const roles = JSON.parse(jsonRoles);
        return roles
          .filter((role: { uuid?: string }) => role.uuid) // Ensure uuid exists
          .map((role: { uuid: string }) => role.uuid)
          .join(',');
      } catch (error) {
        console.error('splitio => Failed to parse roles JSON:', error);
        return '';
      }
    }
    return '';
  }
}
